import type { Monitor, MonitorInput } from './types';
export const intervals = [5, 10, 20, 30, 60];
export const displayState = (monitor: Monitor) => monitor.enabled ? monitor.currentState : 'disabled';
export type MonitorGroup = { name: string | null; monitors: Monitor[] };
const compareLabels = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' }) || a.localeCompare(b);
export function monitorGroups(monitors: Monitor[]): MonitorGroup[] {
  const grouped = new Map<string, MonitorGroup>();
  for (const monitor of monitors) {
    const name = monitor.groupName?.trim() || null;
    const key = name?.toLocaleLowerCase() ?? '__ungrouped__';
    const section = grouped.get(key) ?? { name, monitors: [] };
    section.monitors.push(monitor); grouped.set(key, section);
  }
  return [...grouped.values()]
    .filter(section => section.monitors.length)
    .sort((a, b) => a.name === null ? 1 : b.name === null ? -1 : compareLabels(a.name, b.name))
    .map(section => ({ ...section, monitors: [...section.monitors].sort((a, b) => compareLabels(a.label, b.label)) }));
}
export function monitorGroupNames(monitors: Monitor[]) {
  return monitorGroups(monitors).filter(section => section.name !== null).map(section => section.name as string);
}
export function overview(monitors: Monitor[]) {
  const active = monitors.filter(m => m.enabled);
  if (!active.length) return 'No active monitors';
  if (active.some(m => m.currentState === 'down')) return 'Action needed';
  if (active.some(m => m.currentState !== 'up')) return 'Health not yet confirmed';
  return 'All active monitors are up';
}
export function dateTime(value?: string | null) { return value ? new Date(value).toLocaleString() : 'Not recorded'; }
export function relativeTime(value?: string | null, now = Date.now()) {
  if (!value || !Number.isFinite(Date.parse(value))) return 'Not checked yet';
  const minutes = Math.floor(Math.max(0, now - Date.parse(value)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
}
export function relativeChecked(value?: string | null, now = Date.now()) {
  const formatted = relativeTime(value, now);
  return formatted === 'Not checked yet' ? formatted : `Last checked ${formatted}`;
}
export function outageDuration(monitor: Monitor, now = Date.now()) {
  if (!monitor.enabled) return 'Monitoring disabled';
  if (monitor.currentState === 'unknown') return 'Not yet known';
  if (monitor.currentState !== 'down') return 'No current outage';
  const started = monitor.outageStartedAt ? Date.parse(monitor.outageStartedAt) : NaN;
  if (!Number.isFinite(started) || started > now) return 'Start time unavailable';
  const seconds = Math.floor((now - started) / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor(seconds / 3600) % 24;
  const minutes = Math.floor(seconds / 60) % 60;
  return `${days ? `${days}d ` : ''}${hours || days ? `${hours}h ` : ''}${minutes}m ${seconds % 60}s`;
}
export function parseMonitor(form: FormData): MonitorInput {
  const label = String(form.get('label') ?? '').trim();
  const description = String(form.get('description') ?? '').trim();
  const groupChoice = String(form.get('groupName') ?? '').trim();
  const groupName = groupChoice === '__new__' ? String(form.get('newGroupName') ?? '').trim() : groupChoice || null;
  const url = String(form.get('url') ?? '').trim();
  let parsed: URL;
  try { parsed = new URL(url); } catch { throw new Error('Enter a valid HTTP or HTTPS URL.'); }
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password || parsed.hash || (parsed.port && !['80', '443'].includes(parsed.port)) || url.length > 2048) throw new Error('Use a public HTTP or HTTPS URL on port 80 or 443, without credentials or fragments.');
  if (!label || label.length > 120) throw new Error('Enter a monitor name of 1–120 characters.');
  if (description.length > 500) throw new Error('Description must be 500 characters or fewer.');
  if (groupName && groupName.length > 80) throw new Error('Group name must be 80 characters or fewer.');
  if (groupChoice === '__new__' && !groupName) throw new Error('Enter a name for the new group.');
  const intervalMinutes = Number(form.get('intervalMinutes'));
  const timeoutSeconds = Number(form.get('timeoutSeconds'));
  if (!intervals.includes(intervalMinutes)) throw new Error('Choose a supported check interval.');
  if (!Number.isInteger(timeoutSeconds) || timeoutSeconds < 1 || timeoutSeconds > 10) throw new Error('Timeout must be between 1 and 10 seconds.');
  const codes = String(form.get('expectedStatusCodes') ?? '').trim();
  const expectedStatusCodes = codes ? [...new Set(codes.split(',').map(value => Number(value.trim())))] : undefined;
  if (expectedStatusCodes?.some(code => !Number.isInteger(code) || code < 100 || code > 599)) throw new Error('Expected status codes must be comma-separated numbers between 100 and 599.');
  return { label, description, groupName, url, intervalMinutes, timeoutSeconds, expectedStatusCodes: expectedStatusCodes ?? Array.from({ length: 200 }, (_, index) => index + 200) };
}
